import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './HighlightsForm.css';

export default function AdmissionsFeesPaymentForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({ title: '', subtitle: '', description: '', blocks: [] });
  const [pendingFields, setPendingFields] = useState({ title: false, subtitle: false, description: false });
  const [pendingFees, setPendingFees] = useState({});
  const [originalData, setOriginalData] = useState({...formData});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [selectedFeeForModal, setSelectedFeeForModal] = useState(null);
  const [tempItem, setTempItem] = useState({});

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/admissions/cms/sections`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error(response.status);
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const section = result.data.find(s => s.section_type === 'admissions-fees-payment');
        if (section) {
          setSectionId(section.id);
          let content = section.content_published;
          if (typeof content === 'string') content = JSON.parse(content);
          const en = content?.en || {};
          const newData = {
            title: en.title || '',
            subtitle: en.subtitle || '',
            description: en.description || '',
            blocks: Array.isArray(en.blocks) ? en.blocks : (Array.isArray(en.feesPayment) ? en.feesPayment : [])
          };
          setFormData(newData);
          setOriginalData(newData);

          const pRes = await fetch(`${apiBase}/cms/section/${section.id}/with-pending`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          });
          if (pRes.ok) {
            const pResult = await pRes.json();
            if (pResult.success && pResult.data?.pending_changes) {
              const pMap = { title: false, subtitle: false, description: false };
              const fMap = {};
              pResult.data.pending_changes.forEach(c => {
                if (c.status === 'pending' && c.locale === 'en') {
                  if (['title', 'subtitle', 'description'].includes(c.field_id)) pMap[c.field_id] = true;
                  else if (c.field_id?.includes('blocks.')) {
                    const m = c.field_id.match(/blocks\.(\d+)\./);
                    if (m) fMap[`fee_${m[1]}`] = true;
                  }
                }
              });
              setPendingFields(pMap);
              setPendingFees(fMap);
            }
          }
        }
      }
    } catch (err) {
      console.error('[AdmissionsFeesPaymentForm] Load error:', err.message);
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (name, value) => setFormData(prev => ({...prev, [name]: value}));
  
  const handleFeeChange = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      blocks: prev.blocks.map((f, i) => i === idx ? {...f, [field]: value} : f)
    }));
  };

  const handleAddItem = (idx, type) => {
    if (!tempItem[`${idx}-${type}`]?.trim()) return;
    setFormData(prev => ({
      ...prev,
      blocks: prev.blocks.map((f, i) => i === idx ? {...f, [type]: [...(f[type] || []), tempItem[`${idx}-${type}`].trim()]} : f)
    }));
    setTempItem(p => ({...p, [`${idx}-${type}`]: ''}));
  };

  const handleRemoveItem = (idx, type, itemIdx) => {
    setFormData(prev => ({
      ...prev,
      blocks: prev.blocks.map((f, i) => i === idx ? {...f, [type]: f[type].filter((_, ti) => ti !== itemIdx)} : f)
    }));
  };

  const handleSubmitField = async (name) => {
    if (!sectionId) { toast.error('No section'); return; }
    if (!formData[name]) { toast.warning(`Fill ${name}`); return; }
    if (formData[name] === originalData[name]) { toast.warning('No changes'); return; }

    setSubmitting(p => ({...p, [name]: true}));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: name,
          field_name: name,
          old_value: originalData[name],
          new_value: formData[name],
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || res.ok) {
        toast.success('Submitted');
        setPendingFields(p => ({...p, [name]: true}));
        setOriginalData(p => ({...p, [name]: formData[name]}));
      } else toast.error(result.error || 'Failed');
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({...p, [name]: false}));
    }
  };

  const handleSubmitFee = async (idx) => {
    if (!sectionId) { toast.error('No section'); return; }
    const fee = formData.blocks[idx];
    if (!fee?.title) { toast.warning('Fill title'); return; }

    const key = `fee_${idx}`;
    setSubmitting(p => ({...p, [key]: true}));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `blocks.${idx}.update`,
          field_name: `Fee: ${fee.title}`,
          old_value: originalData.blocks[idx] || {},
          new_value: fee,
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || res.ok) {
        toast.success('Fee submitted');
        setPendingFees(p => ({...p, [key]: true}));
        setOriginalData(p => ({...p, blocks: p.blocks.map((f, i) => i === idx ? fee : f)}));
      } else toast.error(result.error || 'Failed');
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({...p, [key]: false}));
    }
  };

  const handleDeleteFee = (idx) => {
    if (window.confirm('Delete fee section?')) {
      setFormData(prev => ({...prev, blocks: prev.blocks.filter((_, i) => i !== idx)}));
      setPendingFees(p => { const u = {...p}; delete u[`fee_${idx}`]; return u; });
    }
  };

  const handleAddFee = () => {
    setFormData(prev => ({
      ...prev,
      blocks: [...prev.blocks, { title: '', icon: '', summary: '', description: '', items: [], highlight: '' }]
    }));
  };

  if (loading) return <div className="hero-form-wrapper"><div className="loading">Loading...</div></div>;

  return (
    <div className="hero-form-wrapper">
      <ToastContainer />
      <div className="hero-form-header">
        <h1 className="hero-form-title">Admissions Fees & Payment</h1>
        <p className="hero-form-description">Edit title, subtitle, description & manage fees & payment information</p>
      </div>

      {/* Title */}
      <div className="form-section">
        <div className="field-header">
          <label>Title</label>
          {pendingFields.title && <span className="status-badge pending">Pending</span>}
        </div>
        <input type="text" value={formData.title} onChange={e => handleFieldChange('title', e.target.value)} disabled={submitting.title || pendingFields.title} placeholder="Section title..." className="text-input" />
        <button onClick={() => handleSubmitField('title')} disabled={submitting.title || pendingFields.title || formData.title === originalData.title} className="submit-btn">
          {submitting.title ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Subtitle */}
      <div className="form-section">
        <div className="field-header">
          <label>Subtitle</label>
          {pendingFields.subtitle && <span className="status-badge pending">Pending</span>}
        </div>
        <input type="text" value={formData.subtitle} onChange={e => handleFieldChange('subtitle', e.target.value)} disabled={submitting.subtitle || pendingFields.subtitle} placeholder="Subtitle..." className="text-input" />
        <button onClick={() => handleSubmitField('subtitle')} disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle} className="submit-btn">
          {submitting.subtitle ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Description */}
      <div className="form-section">
        <div className="field-header">
          <label>Description</label>
          {pendingFields.description && <span className="status-badge pending">Pending</span>}
        </div>
        <textarea value={formData.description} onChange={e => handleFieldChange('description', e.target.value)} disabled={submitting.description || pendingFields.description} placeholder="Description..." className="textarea-input" rows="3" />
        <button onClick={() => handleSubmitField('description')} disabled={submitting.description || pendingFields.description || formData.description === originalData.description} className="submit-btn">
          {submitting.description ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Fees Payment */}
      <div className="form-section">
        <div className="items-section-header">
          <h2 className="form-section-title">Fee Sections</h2>
          <button type="button" onClick={handleAddFee} className="btn-add-item">+ Add Fee</button>
        </div>

        {formData.blocks.length === 0 ? (
          <div className="items-empty-state"><p>No fee sections. Click "Add Fee"</p></div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1.5rem' }}>
            {formData.blocks.map((fee, idx) => {
              const key = `fee_${idx}`;
              const isPending = pendingFees[key];
              return (
                <div key={idx} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  {/* Card Content */}
                  <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1 }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#1f2937' }}>
                      {fee.title || 'Untitled'}
                    </h3>
                    {fee.summary && <p style={{ margin: 0, fontSize: '0.875rem', color: '#4b5563', lineHeight: 1.5 }}>{fee.summary.substring(0, 70)}...</p>}
                    {fee.items && fee.items.length > 0 && (
                      <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>{fee.items.length} items</p>
                    )}
                  </div>

                  {/* Card Actions */}
                  <div style={{ padding: '0.75rem', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '0.5rem' }}>
                    <button 
                      type="button"
                      onClick={() => setSelectedFeeForModal(idx)}
                      disabled={isPending}
                      style={{ flex: 1, padding: '0.5rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
                    >
                      Edit
                    </button>
                    <button 
                      type="button"
                      onClick={() => handleDeleteFee(idx)}
                      disabled={isPending}
                      style={{ flex: 1, padding: '0.5rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
                    >
                      Delete
                    </button>
                  </div>

                  {isPending && <div style={{ padding: '0.5rem', background: '#fef08a', color: '#92400e', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600' }}>Pending Approval</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Fee Modal */}
      {selectedFeeForModal !== null && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }} onClick={() => setSelectedFeeForModal(null)}>
          <div style={{ background: 'white', borderRadius: '12px', maxHeight: '90vh', overflow: 'auto', maxWidth: '700px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>Edit Fee Section</h2>
              <button type="button" onClick={() => setSelectedFeeForModal(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Title */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Title *</label>
                <input 
                  type="text" 
                  value={formData.blocks[selectedFeeForModal]?.title || ''} 
                  onChange={e => handleFeeChange(selectedFeeForModal, 'title', e.target.value)} 
                  placeholder="e.g., Registration & Admission Fees" 
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Icon */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Icon (Font Awesome)</label>
                <input 
                  type="text" 
                  value={formData.blocks[selectedFeeForModal]?.icon || ''} 
                  onChange={e => handleFeeChange(selectedFeeForModal, 'icon', e.target.value)} 
                  placeholder="e.g., fa-clipboard-list" 
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Summary */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Summary</label>
                <textarea 
                  value={formData.blocks[selectedFeeForModal]?.summary || ''} 
                  onChange={e => handleFeeChange(selectedFeeForModal, 'summary', e.target.value)} 
                  placeholder="Short summary..." 
                  rows="2"
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Description */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Description</label>
                <textarea 
                  value={formData.blocks[selectedFeeForModal]?.description || ''} 
                  onChange={e => handleFeeChange(selectedFeeForModal, 'description', e.target.value)} 
                  placeholder="Full description..." 
                  rows="3"
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Items */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Items</label>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input 
                    type="text" 
                    value={tempItem[`${selectedFeeForModal}-items`] || ''} 
                    onChange={e => setTempItem(p => ({...p, [`${selectedFeeForModal}-items`]: e.target.value}))}
                    placeholder="Add item..."
                    style={{ flex: 1, padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                  />
                  <button 
                    type="button" 
                    onClick={() => handleAddItem(selectedFeeForModal, 'items')}
                    style={{ padding: '0.75rem 1.5rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Add
                  </button>
                </div>
                {formData.blocks[selectedFeeForModal]?.items && formData.blocks[selectedFeeForModal].items.length > 0 && (
                  <ul style={{ margin: 0, paddingLeft: '1.25rem', listStyle: 'disc' }}>
                    {formData.blocks[selectedFeeForModal].items.map((item, ii) => (
                      <li key={ii} style={{ fontSize: '0.875rem', marginBottom: '0.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{item}</span>
                        <button 
                          type="button" 
                          onClick={() => handleRemoveItem(selectedFeeForModal, 'items', ii)}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Highlight */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Highlight/Key Point</label>
                <textarea 
                  value={formData.blocks[selectedFeeForModal]?.highlight || ''} 
                  onChange={e => handleFeeChange(selectedFeeForModal, 'highlight', e.target.value)} 
                  placeholder="Important point to highlight..." 
                  rows="2"
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Modal Actions */}
              <div style={{ display: 'flex', gap: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                <button 
                  type="button"
                  onClick={() => handleSubmitFee(selectedFeeForModal)}
                  disabled={submitting[`fee_${selectedFeeForModal}`]}
                  style={{ flex: 1, padding: '0.75rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  {submitting[`fee_${selectedFeeForModal}`] ? 'Submitting...' : 'Submit'}
                </button>
                <button 
                  type="button"
                  onClick={() => setSelectedFeeForModal(null)}
                  style={{ flex: 1, padding: '0.75rem', background: '#6b7280', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

